import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Bell, Ruler, Images, FolderOpen, CheckCircle2, CalendarClock, TrendingUp, Video, MessageCircle } from "lucide-react";
import { planInfo } from "@/lib/plan";

const patientName = (p) => [p.cognome, p.nome].filter(Boolean).join(" ") || "Senza nome";
const progReason = (g) => {
  const when = g.last ? ` · ultima ${new Date(g.last).toLocaleDateString("it-IT", { day: "2-digit", month: "short" })}` : "";
  return `${g.count} da confermare · ${g.exercises.join(", ")}${when}`;
};

const hasMeasurements = (p) => {
  const m = p.measurements || {};
  const weeks = [...(m.antropo || []), ...(m.plico || [])];
  return weeks.some((w) => w.values && Object.values(w.values).some((v) => v !== "" && v != null));
};

// Photo request is due the day before one month after the last upload date.
const photoDue = (p) => {
  const sections = (p.foto && p.foto.sections) || [];
  const dates = sections.map((s) => s.date).filter(Boolean).map((d) => new Date(d)).filter((d) => !isNaN(d));
  if (dates.length === 0) return { due: true, reason: "Nessuna foto caricata" };
  const last = new Date(Math.max(...dates.map((d) => d.getTime())));
  const nextReq = new Date(last);
  nextReq.setMonth(nextReq.getMonth() + 1);
  const notifyFrom = new Date(nextReq);
  notifyFrom.setDate(notifyFrom.getDate() - 1);
  if (new Date() >= notifyFrom) {
    return { due: true, reason: `Foto da richiedere (ultima: ${last.toLocaleDateString("it-IT")})` };
  }
  return { due: false, next: notifyFrom, reason: `Prossimo avviso: ${notifyFrom.toLocaleDateString("it-IT")}` };
};

export function computeReminders(patients, pendingProgress = [], pendingVideos = [], pendingComments = [], pendingMeasures = []) {
  const measurements = [];
  const photos = [];
  const plans = [];
  patients.forEach((p) => {
    if (!hasMeasurements(p)) measurements.push({ id: p.id, name: patientName(p), reason: "Nessuna misurazione registrata" });
    const pd = photoDue(p);
    if (pd.due) photos.push({ id: p.id, name: patientName(p), reason: pd.reason });
    const pi = planInfo(p);
    if (pi.hasPlan && pi.expiringSoon) {
      const reason = pi.expired
        ? `Scaduto da ${Math.abs(pi.daysLeft)} giorni (${pi.end.toLocaleDateString("it-IT")})`
        : `Scade tra ${pi.daysLeft} giorni (${pi.end.toLocaleDateString("it-IT")})`;
      plans.push({ id: p.id, name: patientName(p), reason });
    }
  });
  const ids = new Set(patients.map((p) => p.id));
  const progress = (pendingProgress || []).filter((g) => ids.has(g.patientId)).map((g) => {
    const p = patients.find((x) => x.id === g.patientId);
    return { id: g.patientId, name: p ? patientName(p) : g.patientName, reason: progReason(g) };
  });
  const videos = (pendingVideos || []).filter((v) => ids.has(v.patientId)).map((v) => ({
    id: `${v.patientId}|${v.videoId}`, name: v.patientName || "Paziente",
    reason: `${v.exercise || "Esercizio"} · ${v.created_at ? new Date(v.created_at).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" }) : ""}`,
  }));
  const comments = (pendingComments || []).filter((g) => ids.has(g.patientId)).map((g) => {
    const p = patients.find((x) => x.id === g.patientId);
    const when = g.last ? ` · ${new Date(g.last).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" })}` : "";
    return { id: g.patientId, name: p ? patientName(p) : g.patientName, reason: `${g.count} ${g.count === 1 ? "nuova nota" : "nuove note"} · ${g.exercises.join(", ")}${when}` };
  });
  const received = (pendingMeasures || []).filter((m) => ids.has(m.patient_id)).map((m) => ({
    id: `${m.patient_id}|${m.id}`, name: m.patientName || "Paziente",
    reason: `Misure del ${new Date(m.date).toLocaleDateString("it-IT")} · inviate ${new Date(m.created_at).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" })}`,
  }));
  return { measurements, photos, plans, progress, videos, comments, received, total: measurements.length + photos.length + plans.length + progress.length + videos.length + comments.length + received.length, isSunday: new Date().getDay() === 0 };
}

const Section = ({ icon: Icon, title, subtitle, items, onOpen, emptyText, color, targetTab }) => (
  <div className="space-y-2">
    <div className="flex items-center gap-2">
      <Icon className="h-4 w-4" style={{ color }} />
      <h3 className="font-display text-sm font-bold">{title}</h3>
      <span className="rounded-md px-1.5 py-0.5 text-[11px] font-bold" style={{ background: `${color}22`, color }}>{items.length}</span>
    </div>
    <p className="text-[11px] text-muted-foreground">{subtitle}</p>
    {items.length === 0 ? (
      <div className="flex items-center gap-2 rounded-lg border border-white/5 bg-black/20 px-3 py-2.5 text-sm text-muted-foreground">
        <CheckCircle2 className="h-4 w-4 text-primary" /> {emptyText}
      </div>
    ) : (
      <div className="space-y-1.5">
        {items.map((it) => (
          <div key={it.id} className="flex items-center justify-between gap-2 rounded-lg border border-white/10 bg-black/20 px-3 py-2" data-testid={`notif-item-${it.id}`}>
            <div className="min-w-0">
              <div className="truncate text-sm font-semibold">{it.name}</div>
              <div className="text-[11px] text-muted-foreground">{it.reason}</div>
            </div>
            <button onClick={() => onOpen(it.id, targetTab)} className="shrink-0 rounded-md border border-white/10 p-2 text-muted-foreground transition-colors hover:text-primary" title="Apri paziente" data-testid={`notif-open-${it.id}`}>
              <FolderOpen className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>
    )}
  </div>
);

export default function NotificheDrawer({ open, setOpen, patients, pendingProgress = [], pendingVideos = [], pendingComments = [], pendingMeasures = [], onOpenPatient }) {
  const { measurements, photos, plans, progress, videos, comments, received, isSunday } = computeReminders(patients, pendingProgress, pendingVideos, pendingComments, pendingMeasures);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent side="right" className="w-full border-white/10 bg-[hsl(var(--card))] text-foreground sm:max-w-md" data-testid="notifiche-drawer">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2 font-display text-foreground">
            <Bell className="h-5 w-5 text-primary" /> Notifiche
          </SheetTitle>
        </SheetHeader>

        <div className="mt-5 space-y-6 overflow-y-auto pb-6" style={{ maxHeight: "calc(100vh - 120px)" }}>
          {patients.length === 0 ? (
            <div className="rounded-lg border border-dashed border-white/10 py-10 text-center text-sm text-muted-foreground">
              Nessun paziente registrato. Salva un paziente per attivare i promemoria.
            </div>
          ) : (
            <>
              <Section
                icon={Ruler}
                title="MISURAZIONI RICEVUTE"
                subtitle="Misure inviate dai clienti dal link personale: media dei 3 valori per parametro, da confermare in Sezione 5."
                items={received}
                onOpen={onOpenPatient}
                targetTab="measures"
                emptyText="Nessuna misurazione ricevuta"
                color="hsl(43 74% 58%)"
              />
              <Section
                icon={Video}
                title="Video da analizzare"
                subtitle="Video caricati dai pazienti dal link personale, in attesa della tua correzione."
                items={videos}
                onOpen={onOpenPatient}
                targetTab="video"
                emptyText="Nessun video da analizzare"
                color="hsl(43 74% 58%)"
              />
              <Section
                icon={MessageCircle}
                title="Note di allenamento dei clienti"
                subtitle="Commenti scritti dai clienti sugli esercizi dalla scheda condivisa."
                items={comments}
                onOpen={onOpenPatient}
                targetTab="comments"
                emptyText="Nessuna nuova nota"
                color="hsl(258 90% 76%)"
              />
              <Section
                icon={TrendingUp}
                title="Progressioni da confermare"
                subtitle="Progressioni salvate dai pazienti tramite la scheda condivisa, in attesa di conferma."
                items={progress}
                onOpen={onOpenPatient}
                targetTab={8}
                emptyText="Nessuna progressione da confermare"
                color="hsl(199 89% 60%)"
              />
              <Section
                icon={Ruler}
                title="Misurazioni mancanti"
                subtitle={`Promemoria settimanale (ogni domenica).${isSunday ? " Oggi è domenica: controlla questi clienti." : ""}`}
                items={measurements}
                onOpen={onOpenPatient}
                targetTab={5}
                emptyText="Tutti i clienti hanno misurazioni registrate"
                color="hsl(var(--c-teal))"
              />
              <Section
                icon={Images}
                title="Foto da richiedere"
                subtitle="Promemoria mensile: avviso il giorno prima del mese successivo all'ultimo caricamento."
                items={photos}
                onOpen={onOpenPatient}
                targetTab={7}
                emptyText="Nessuna foto da richiedere al momento"
                color="hsl(var(--c-fat))"
              />
              <Section
                icon={CalendarClock}
                title="Piani in scadenza"
                subtitle="Avviso quando mancano 14 giorni o meno alla scadenza del piano nutrizionale."
                items={plans}
                onOpen={onOpenPatient}
                targetTab="report"
                emptyText="Nessun piano in scadenza"
                color="hsl(0 72% 62%)"
              />
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
